import type { Analysis } from '@trainme/db/client';
import { endOfDay, startOfDay } from 'date-fns';
import { create } from 'zustand';
import { createMapArrayCache } from './mapArrayCache';

interface AnalysisState {
  analyses: Map<string, Analysis>;
  lastSyncAt: number;
  mergeAnalyses: (newAnalyses: Analysis[]) => void;
  removeAnalysis: (analysisId: string) => void;
  removeAnalysesByWorkoutId: (workoutId: string) => void;
  clearAnalyses: () => void;
  getFilteredAnalyses: (range?: { start: Date; end: Date }) => Analysis[];
}

const getAnalysesArray = createMapArrayCache<string, Analysis>();

export const useAnalysisStore = create<AnalysisState>((set, get) => ({
  analyses: new Map(),
  lastSyncAt: 0,

  mergeAnalyses: (newAnalyses) => {
    set((state) => {
      const updatedMap = new Map(state.analyses);
      newAnalyses.forEach((analysis) => {
        updatedMap.set(analysis.id.toString(), analysis);
      });
      return { analyses: updatedMap };
    });
  },

  removeAnalysis: (analysisId) => {
    set((state) => {
      const updatedMap = new Map(state.analyses);
      updatedMap.delete(analysisId);
      return { analyses: updatedMap };
    });
  },

  removeAnalysesByWorkoutId: (workoutId) => {
    set((state) => {
      const updatedMap = new Map(state.analyses);
      // Remove all analyses that match this workoutId
      for (const [id, analysis] of updatedMap.entries()) {
        if (analysis.workoutId === workoutId) {
          updatedMap.delete(id);
        }
      }
      return { analyses: updatedMap };
    });
  },

  clearAnalyses: () => {
    set({ analyses: new Map(), lastSyncAt: Date.now() });
  },

  getFilteredAnalyses: (range) => {
    const analyses = getAnalysesArray(get().analyses);

    if (!range) {
      return analyses;
    }

    const rangeStart = startOfDay(range.start);
    const rangeEnd = endOfDay(range.end);

    return analyses.filter((analysis) => {
      if (!analysis.date) return false;
      const analysisDate =
        typeof analysis.date === 'string'
          ? new Date(analysis.date)
          : analysis.date;
      return analysisDate >= rangeStart && analysisDate <= rangeEnd;
    });
  },
}));
